import { Code2, ExternalLink, KeyRound, Plus, RotateCw, Trash2 } from "lucide-react";
import { useState } from "react";
import { errorMessage, type S } from "../api/client";
import { useOrg, useOrgId } from "../api/org";
import { useCreateEmbedKey, useDeleteEmbedKey, useEmbedKeys, useUpdateEmbedKey } from "../api/queries";
import { InstallSnippets } from "../components/InstallSnippets";
import { OriginsEditor } from "../components/OriginsEditor";
import { Alert, Badge, Button, Card, ChipsInput, CopyButton, Empty, Field, Modal, PageHead, Segmented, Spinner, Switch, TextInput, cx, useToast } from "../components/ui";
import { relative } from "../lib/format";
import { normalizeOrigin, originProblem, widgetSrc } from "../lib/snippets";

function NewKeyModal({ orgId, onClose, onCreated }: { orgId: string; onClose: () => void; onCreated: (key: S["EmbedKey"]) => void }) {
  const create = useCreateEmbedKey(orgId);
  const [label, setLabel] = useState("");
  const [mode, setMode] = useState<S["EmbedKeyMode"]>("live");
  const [origins, setOrigins] = useState<string[]>([]);
  return (
    <Modal
      title="New embed key"
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            loading={create.isPending}
            onClick={() => create.mutate({ label: label || undefined, mode, allowed_origins: origins }, { onSuccess: (k) => onCreated(k) })}
          >
            Create key
          </Button>
        </>
      }
    >
      <div className="stack">
        {create.error && <Alert tone="danger">{errorMessage(create.error)}</Alert>}
        <Field label="Label" hint="For you: e.g. “Main website” or “Landing page”.">
          <TextInput value={label} onChange={(e) => setLabel(e.target.value)} placeholder="Main website" />
        </Field>
        <Field label="Mode" hint={mode === "test" ? "Test keys also work on localhost — use them while building your site." : "Live keys work only on the websites you list."}>
          <Segmented value={mode} onChange={setMode} options={[{ value: "live", label: "Live" }, { value: "test", label: "Test" }]} />
        </Field>
        <Field label="Allowed websites">
          <ChipsInput values={origins} onChange={setOrigins} placeholder="www.your-website.com" normalize={normalizeOrigin} validate={originProblem} />
        </Field>
      </div>
    </Modal>
  );
}

/** Whether the hosted page may be framed here (its CSP frame-ancestors: the key's origins, localhost for test keys). */
function canFrame(key: S["EmbedKey"]): boolean {
  const here = window.location.origin;
  const local = /^https?:\/\/(localhost|127\.0\.0\.1)(:\d+)?$/.test(here);
  if (key.mode === "test" && local) return true;
  return key.allowed_origins.some((o) => {
    if (o === here) return true;
    const [scheme, rest] = o.split("*.");
    return rest !== undefined && here.startsWith(scheme!) && here.endsWith(`.${rest}`);
  });
}

export function Widget() {
  const orgId = useOrgId();
  const { canManage } = useOrg();
  const keys = useEmbedKeys(orgId);
  const update = useUpdateEmbedKey(orgId);
  const remove = useDeleteEmbedKey(orgId);
  const toast = useToast();
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [previewNonce, setPreviewNonce] = useState(0);

  if (keys.isLoading) return <Spinner />;
  if (keys.error) return <Alert tone="danger">{errorMessage(keys.error)}</Alert>;
  const items = keys.data?.items ?? [];
  const selected = items.find((k) => k.id === selectedId) ?? items.find((k) => k.active) ?? items[0];

  return (
    <>
      <PageHead
        title="Website widget"
        description="Publishable keys for the quote calculator on your websites, the code to embed it, and a live preview."
        actions={
          canManage && (
            <Button variant="primary" icon={<Plus />} onClick={() => setCreating(true)}>
              New key
            </Button>
          )
        }
      />
      {!items.length ? (
        <Card>
          <Empty icon={<KeyRound />} title="No embed keys" action={canManage && <Button variant="primary" icon={<Plus />} onClick={() => setCreating(true)}>Create your first key</Button>}>
            A key connects the calculator on your website to your SolarOS account.
          </Empty>
        </Card>
      ) : (
        <div className="stack-lg">
          <div className="stack">
            {items.map((k) => (
              <Card key={k.id} className={cx(k.id === selected?.id && "selected")} pad={false}>
                <div className="card-head" style={{ borderBottom: 0, paddingBottom: 6 }}>
                  <div className="row" style={{ gap: 10 }}>
                    <h2>{k.label || "Untitled key"}</h2>
                    <Badge tone={k.mode === "live" ? "ok" : "warn"}>{k.mode}</Badge>
                    {!k.active && <Badge tone="danger">inactive</Badge>}
                    <span className="small faint">Last used {relative(k.last_used_at)}</span>
                  </div>
                  <div className="row">
                    <Button size="sm" variant={k.id === selected?.id ? "primary" : "default"} icon={<Code2 />} onClick={() => setSelectedId(k.id)}>
                      {k.id === selected?.id ? "Previewing" : "Use for preview"}
                    </Button>
                    {canManage && (
                      <>
                        <Switch
                          checked={k.active}
                          onChange={(active) =>
                            update.mutate({ id: k.id, body: { active } }, { onSuccess: () => toast(active ? "Key activated" : "Key deactivated"), onError: (e) => toast(errorMessage(e), true) })
                          }
                          label={<span className="small muted">Active</span>}
                        />
                        <Button
                          size="sm"
                          variant="danger"
                          icon={<Trash2 />}
                          aria-label="Delete key"
                          onClick={() => {
                            if (window.confirm(`Delete “${k.label || k.key}”? Websites using it stop working immediately.`))
                              remove.mutate(k.id, { onSuccess: () => toast("Key deleted"), onError: (e) => toast(errorMessage(e), true) });
                          }}
                        />
                      </>
                    )}
                  </div>
                </div>
                <div className="card-body grid-2" style={{ paddingTop: 8 }}>
                  <Field label="Key">
                    <div className="keybox">
                      <KeyRound size={16} color="var(--primary)" />
                      <code>{k.key}</code>
                      <CopyButton text={k.key} />
                    </div>
                  </Field>
                  <Field label="Allowed websites">
                    <OriginsEditor orgId={orgId} embedKey={k} canManage={canManage} compact />
                  </Field>
                </div>
              </Card>
            ))}
          </div>

          {selected && (
            <div className="split">
              <Card title="Embed code" description={`For “${selected.label || selected.key}”.`}>
                <InstallSnippets embedKey={selected.key} />
                {!selected.allowed_origins.length && (
                  <div style={{ marginTop: 14 }}>
                    <Alert tone="warn" title="No websites allowed yet.">
                      Add your website above, or the calculator will refuse to load there.
                    </Alert>
                  </div>
                )}
              </Card>
              <div className="stack">
                <div className="spread">
                  <h2>Live preview</h2>
                  <div className="row">
                    <Button size="sm" variant="ghost" icon={<RotateCw />} onClick={() => setPreviewNonce((n) => n + 1)}>
                      Reload
                    </Button>
                    <a className="btn btn-sm" href={widgetSrc(selected.key)} target="_blank" rel="noreferrer">
                      <ExternalLink /> Open
                    </a>
                  </div>
                </div>
                <div className="preview-frame">
                  <div className="preview-bar">
                    <i />
                    <i />
                    <i />
                    <span>{selected.allowed_origins[0] ?? "your-website.com"}</span>
                  </div>
                  {canFrame(selected) ? (
                    <iframe key={`${selected.id}-${previewNonce}`} src={widgetSrc(selected.key)} title="Calculator preview" loading="lazy" />
                  ) : (
                    <Empty
                      icon={<ExternalLink />}
                      title="Preview opens in a new tab"
                      action={
                        <a className="btn btn-primary" href={widgetSrc(selected.key)} target="_blank" rel="noreferrer">
                          <ExternalLink /> Open the calculator
                        </a>
                      }
                    >
                      The calculator only shows inside the websites allowed on this key, so it cannot be framed here. Test keys also
                      preview on localhost.
                    </Empty>
                  )}
                </div>
                <p className="hint">The preview runs the real calculator with this key: finishing a quote uses a design credit.</p>
              </div>
            </div>
          )}
        </div>
      )}
      {creating && (
        <NewKeyModal
          orgId={orgId}
          onClose={() => setCreating(false)}
          onCreated={(k) => {
            setCreating(false);
            setSelectedId(k.id);
            toast("Key created");
          }}
        />
      )}
    </>
  );
}
